// @ts-check

import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ProteinViewerDialog, useProteinViewerDialogContext, DEFAULT_REPRESENTATIONS } from "@/components/ui/protein-viewer-dialog";
import { Toggle } from "@/components/ui/toggle";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { ButtonGroup } from "@/components/ui/button-group";
import { Field, FieldGroup, FieldLabel } from "@/components/ui/field";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { Download, Camera, Loader2, Crosshair, RefreshCcwDot, Eye, EyeOff, Trash2, Plus, RotateCcw, Pencil, Check, X, ChevronLeft, ChevronRight } from "lucide-react";
import { GiMolecule } from "react-icons/gi";
import { randomUUID } from "@minohealth/common/utils/random";
import { useSelector } from "react-redux";
import { getSpacesPresignedUrl } from "@minohealth/common/services/SpacesService";

/** @param {string} fileUrl */
function useProteinFile(fileUrl) {
  const { user } = useSelector((state) => state.app.datastate)
  return useQuery({
    queryKey: ['protein-file', fileUrl],
    queryFn: () => getSpacesPresignedUrl({ objectUrl: fileUrl, userToken: user?.userToken }),
    enabled: !!fileUrl,
    staleTime: 50 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  })
}

/**
 * Format filename from URL for display
 * @param {string} url
 * @returns {string}
 */
function formatFilename(url) {
  const filename = url.split("/").pop() || url
  return filename.replace(/_/g, " ")
}

/**
 * Hook for managing file navigation state
 * @param {string[]} fileUrls - Array of file URLs
 * @param {string | null} defaultFileUrl - Initial file URL to select
 */
function useFileNavigation(fileUrls, defaultFileUrl) {
  const [currentIndex, setCurrentIndex] = useState(() => {
    if (!defaultFileUrl || fileUrls.length === 0) return 0
    const index = fileUrls.findIndex(url => url === defaultFileUrl)
    return index >= 0 ? index : 0
  })

  const currentFileUrl = fileUrls[currentIndex] ?? null
  const hasPrev = currentIndex > 0
  const hasNext = currentIndex < fileUrls.length - 1

  const goToPrev = () => {
    if (hasPrev) setCurrentIndex(i => i - 1)
  }

  const goToNext = () => {
    if (hasNext) setCurrentIndex(i => i + 1)
  }

  return {
    currentFileUrl,
    currentIndex,
    setCurrentIndex,
    hasPrev,
    hasNext,
    goToPrev,
    goToNext,
  }
}

function ProteinCenterStageAction() {
  const { stage } = useProteinViewerDialogContext()

  function centerStage() {
    if (stage == null) return
    stage.autoView(500)
  }

  return (
    <ProteinViewerDialog.Action
      tooltip="Center Stage"
      disabled={stage == null}
      onClick={() => centerStage()}
    >
      <Crosshair />
      <span className="sr-only">Center Stage</span>
    </ProteinViewerDialog.Action>
  )
}

function ProteinAutoSpinAction() {
  const { stage } = useProteinViewerDialogContext()
  const [autoSpinEnabled, setIsAutoSpinEnabled] = useState(true)

  function toggleAutoSpin() {
    setIsAutoSpinEnabled((enabled) => !enabled)
  }

  /**
    * @param {import("@/components/ui/protein-viewer-dialog").ProteinViewerDialogContextValue["stage"]} stage
    * @param {boolean} autoSpinEnabled
    */
  function handleAutoSpin(stage, autoSpinEnabled) {
    if (stage == null) return

    stage.setSpin(autoSpinEnabled)
  }

  useEffect(() => handleAutoSpin(stage, autoSpinEnabled), [stage, autoSpinEnabled])

  return (
    <ProteinViewerDialog.ToggleAction
      tooltip={`Auto Spin: ${autoSpinEnabled ? "On" : "Off"}`}
      disabled={stage == null}
      pressed={autoSpinEnabled}
      onClick={() => toggleAutoSpin()}
    >
      <RefreshCcwDot className="size-4.5" />
      <span className="sr-only">Auto Spin</span>
    </ProteinViewerDialog.ToggleAction>
  )
}

function ProteinDownloadAction() {
  const { file } = useProteinViewerDialogContext()
  const [downloadUrl, setDownloadUrl] = useState(/**@type{string|null}*/(null))

  const filename = (() => {
    if (typeof file === "string") return "" // TODO:
    if (file instanceof File) return file.name
    return null
  })()

  /** @param {import("@/components/ui/protein-viewer-dialog").ProteinViewerDialogContextValue["file"]} file */
  function setupDownloadUrl(file) {
    if (file == null) return

    if (typeof file === "string") {
      setDownloadUrl(file)
      return () => {
        setupDownloadUrl(null)
      }
    }

    if (file instanceof File) {
      const objectUrl = URL.createObjectURL(file)
      setupDownloadUrl(objectUrl)

      return () => {
        URL.revokeObjectURL(objectUrl)
        setupDownloadUrl(null)
      }
    }
  }

  useEffect(() => setupDownloadUrl(file), [file])

  return (
    <ProteinViewerDialog.Action
      asChild
      tooltip="Download"
      disabled={downloadUrl == null}
    >
      <a href={downloadUrl} download={filename}>
        <Download />
        <span className="sr-only">Download</span>
      </a>
    </ProteinViewerDialog.Action>
  )
}

const PROTEIN_SNAPSHOT_BACKGROUND_COLOR = "#fff"

function ProteinSnapshotAction() {
  const { file, stage } = useProteinViewerDialogContext()
  const [isCapturing, setIsCapturing] = useState(false)

  const filename = (() => {
    if (typeof file === "string") return "" // TODO:
    if (file instanceof File) return file.name
    return null
  })()

  // Snapshot is always generated as image/png
  const snapshotFilename = filename + ".png"

  async function takeSnapshot() {
    if (stage == null) return
    if (isCapturing) return

    setIsCapturing(true)
    const previousStageBgColor = stage.getParameters().backgroundColor
    stage.setParameters({ backgroundColor: PROTEIN_SNAPSHOT_BACKGROUND_COLOR })
    try {
      const blob = await stage.makeImage({
        antialias: true,
        factor: 2,
      })

      const url = URL.createObjectURL(blob);
      const downloadEl = document.createElement("a");
      downloadEl.href = url;
      downloadEl.download = snapshotFilename;
      document.body.appendChild(downloadEl);
      downloadEl.click();
      document.body.removeChild(downloadEl);
      URL.revokeObjectURL(url);
    } catch (error) {
      // Maybe show toast
      console.error(error)
    } finally {
      setIsCapturing(false)
      if(stage.getParameters().backgroundColor === PROTEIN_SNAPSHOT_BACKGROUND_COLOR) {
        stage.setParameters({ backgroundColor: previousStageBgColor })
      }
    }
  }

  return (
    <ProteinViewerDialog.Action
      tooltip="Take Snapshot"
      disabled={stage == null}
      onClick={() => takeSnapshot()}
    >
      {!isCapturing && <Camera />}
      {isCapturing && <Loader2 className="animate-spin" />}
      <span className="sr-only">Take Snapshot</span>
    </ProteinViewerDialog.Action>
  )
}

// TODO: More questions from Gertrude
/** @satisfies {Record<string, import("@/components/ui/protein-viewer-dialog").StructureRepresentation[]>} */
// const PresetRepresentations = {
//   // Binding afinity and docking
//   "": [
//     { type: "cartoon", select: "polymer" },
//     { type: "ball+stick", select: "ligand" },
//   ],
//   // structure prediction
//   "": [
//     { type: "ribbon", select: "polymer" },
//   ],
// }

/** @typedef {import("@/components/ui/protein-viewer-dialog").StructureRepresentation & { id: string, label: string }} RepresentationEntry */

/** @param {import("@/components/ui/protein-viewer-dialog").StructureRepresentation} repr */
function labelFor(repr) {
  /** @type {Record<string, string>} */
  const map = { polymer: "Polymer", ligand: "Ligand", water: "Water", all: "All" }
  return map[repr.select] ?? repr.select.charAt(0).toUpperCase() + repr.select.slice(1)
}

/** @param {import("@/components/ui/protein-viewer-dialog").StructureRepresentation} repr */
function withId(repr) {
  return { id: randomUUID(), label: labelFor(repr), ...repr }
}

const NGL_TYPES = ["cartoon", "ball+stick", "ribbon", "surface", "licorice", "line", "spacefill"]
const NGL_SELECTIONS = ["polymer", "ligand", "water", "all"]

/** @type {Record<string, string>} */
const TYPE_LABELS = {
  "cartoon": "Cartoon",
  "ball+stick": "Ball & Stick",
  "ribbon": "Ribbon",
  "surface": "Surface",
  "licorice": "Licorice",
  "line": "Line",
  "spacefill": "Spacefill",
}

/** @type {{ value: string, label: string }[]} */
const NGL_COLOR_SCHEMES = [
  { value: "residueindex", label: "Residue Index" },
  { value: "chainid", label: "Chain ID" },
  { value: "chainindex", label: "Chain Index" },
  { value: "element", label: "Element (CPK)" },
  { value: "sstruc", label: "Secondary Structure" },
  { value: "bfactor", label: "B-Factor" },
  { value: "hydrophobicity", label: "Hydrophobicity" },
  { value: "entitytype", label: "Entity Type" },
  { value: "moleculetype", label: "Molecule Type" },
  { value: "atomindex", label: "Atom Index" },
  { value: "resname", label: "Residue Name" },
  { value: "occupancy", label: "Occupancy" },
  { value: "uniform", label: "Uniform" },
  { value: "random", label: "Random" },
]

/**
  * @typedef {{ type: string, select: string, color: string, opacity: number }} ReprFormState
  */

/**
  * @param {{
  *   value: ReprFormState,
  *   onChange: (v: ReprFormState) => void,
  *   onConfirm: () => void,
  *   onCancel: () => void,
  *   confirmLabel?: string,
  * }} props
  */
function RepresentationForm({ value, onChange, onConfirm, onCancel, confirmLabel = "Add" }) {
  /** @param {Partial<ReprFormState>} patch */
  function set(patch) { onChange({ ...value, ...patch }) }

  return (
    <div className="flex flex-col gap-3 px-3 py-3 border-b bg-muted/30 shrink-0">
      <FieldGroup className="gap-2">
        <Field className="gap-0.5">
          <FieldLabel className="text-xs text-muted-foreground">Type</FieldLabel>
          <Select value={value.type} onValueChange={v => set({ type: v })}>
            <SelectTrigger className="h-8 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {NGL_TYPES.map(t => <SelectItem key={t} value={t} className="text-xs">{TYPE_LABELS[t] ?? t}</SelectItem>)}
            </SelectContent>
          </Select>
        </Field>

        <Field className="gap-0.5">
          <FieldLabel className="text-xs text-muted-foreground">Selection</FieldLabel>
          <Select value={value.select} onValueChange={v => set({ select: v })}>
            <SelectTrigger className="h-8 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {NGL_SELECTIONS.map(s => <SelectItem key={s} value={s} className="text-xs">{s.charAt(0).toUpperCase() + s.slice(1)}</SelectItem>)}
            </SelectContent>
          </Select>
        </Field>

        <Field className="gap-0.5">
          <FieldLabel className="text-xs text-muted-foreground">Color Scheme</FieldLabel>
          <Select value={value.color} onValueChange={v => set({ color: v })}>
            <SelectTrigger className="h-8 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {NGL_COLOR_SCHEMES.map(c => <SelectItem key={c.value} value={c.value} className="text-xs">{c.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </Field>

        <Field className="gap-1.5">
          <div className="flex items-center justify-between">
            <FieldLabel className="text-xs text-muted-foreground">Opacity</FieldLabel>
            <span className="text-xs text-muted-foreground">{Math.round(value.opacity * 100)}%</span>
          </div>
          <Slider
            min={0} max={1} step={0.05}
            value={[value.opacity]}
            onValueChange={(vals) => set({ opacity: vals[0] })}
          />
        </Field>
      </FieldGroup>

      <div className="flex gap-1 mt-3">
        <Button size="sm" className="flex-1 h-7 text-xs" onClick={onConfirm}>{confirmLabel}</Button>
        <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={onCancel}>Cancel</Button>
      </div>
    </div>
  )
}

/** @returns {ReprFormState} */
function emptyForm() {
  return { type: NGL_TYPES[0], select: NGL_SELECTIONS[0], color: NGL_COLOR_SCHEMES[0].value, opacity: 1 }
}

/** @param {RepresentationEntry} repr */
function reprToForm(repr) {
  return {
    type: repr.type,
    select: repr.select,
    color: repr.color ?? NGL_COLOR_SCHEMES[0].value,
    opacity: repr.opacity ?? 1,
  }
}

/**
  * @param {{
  *   representations: RepresentationEntry[],
  *   onChange: (reprs: RepresentationEntry[]) => void,
  * }} props
  */
function ProteinRepresentationsPanel({ representations, onChange }) {
  const [showAddForm, setShowAddForm] = useState(false)
  const [addForm, setAddForm] = useState(emptyForm)
  const [editingId, setEditingId] = useState(/** @type{string|null} */(null))
  const [editForm, setEditForm] = useState(/** @type{ReprFormState} */(emptyForm()))

  /** @param {string} id */
  function toggleVisible(id) {
    onChange(representations.map(r => r.id === id ? { ...r, visible: !(r.visible ?? true) } : r))
  }

  /** @param {string} id */
  function remove(id) {
    onChange(representations.filter(r => r.id !== id))
    if(editingId === id) setEditingId(null)
  }

  function confirmAdd() {
    const base = /** @type {import("@/components/ui/protein-viewer-dialog").StructureRepresentation} */ ({
      type: addForm.type,
      select: addForm.select,
      color: addForm.color,
      opacity: addForm.opacity,
    })
    onChange([...representations, withId(base)])
    setAddForm(emptyForm())
    setShowAddForm(false)
  }

  /** @param {string} id */
  function startEdit(id) {
    const repr = representations.find(r => r.id === id)
    if(!repr) return
    setEditForm(reprToForm(repr))
    setEditingId(id)
    setShowAddForm(false)
  }

  function confirmEdit() {
    onChange(representations.map(r => r.id === editingId
      ? { ...r, type: editForm.type, select: editForm.select, color: editForm.color, opacity: editForm.opacity }
      : r
    ))
    setEditingId(null)
  }

  function resetToPreset() {
    onChange(DEFAULT_REPRESENTATIONS.map(withId))
    setEditingId(null)
    setShowAddForm(false)
  }

  return (
    <div className="flex flex-col text-sm border-1 border-border rounded-md">
      {/* Toolbar */}
      <div className="flex items-center justify-between px-3 py-2 border-b shrink-0">
        <span className="font-medium text-xs text-muted-foreground uppercase tracking-wide">Representations</span>
        <div className="flex items-center gap-1">
          <Button size="icon-sm" variant="outline" onClick={resetToPreset} title="Reset to preset">
            <RotateCcw />
          </Button>
          <Button size="sm" variant="outline" onClick={() => { setShowAddForm(v => !v); setEditingId(null) }}>
            <Plus />
            Add
          </Button>
        </div>
      </div>

      {/* Add form */}
      {showAddForm && (
        <RepresentationForm
          value={addForm}
          onChange={setAddForm}
          onConfirm={confirmAdd}
          onCancel={() => setShowAddForm(false)}
          confirmLabel="Add"
        />
      )}

      {/* List */}
      <div className="flex-1 overflow-y-auto">
        {representations.length === 0 && (
          <p className="text-xs text-muted-foreground text-center py-6">No representations</p>
        )}
        {representations.map(repr => {
          const visible = repr.visible ?? true
          const isEditing = editingId === repr.id
          return (
            <div key={repr.id} className="border-b last:border-0">
              <div className="flex items-center gap-2 px-3 py-2 hover:bg-muted/30">
                <span className="flex-1 truncate font-medium">{repr.label}</span>
                <span className="text-xs text-muted-foreground shrink-0">{TYPE_LABELS[repr.type] ?? repr.type}</span>
                <ButtonGroup>
                  <Button size="icon-xs" variant="outline" onClick={() => isEditing ? setEditingId(null) : startEdit(repr.id)} title="Edit">
                    <Pencil />
                  </Button>
                  <Toggle
                    variant="outline"
                    size="sm"
                    className={cn("size-6 min-w-0 rounded-md [&_svg:not([class*='size-'])]:size-3")}
                    pressed={visible}
                    onClick={() => toggleVisible(repr.id)}
                    title={visible ? "Hide" : "Show"}
                  >
                    {visible ? <Eye /> : <EyeOff />}
                  </Toggle>
                  <Button size="icon-xs" variant="outline" onClick={() => remove(repr.id)} title="Remove">
                    <Trash2 />
                  </Button>
                </ButtonGroup>
              </div>
              {isEditing && (
                <RepresentationForm
                  value={editForm}
                  onChange={setEditForm}
                  onConfirm={confirmEdit}
                  onCancel={() => setEditingId(null)}
                  confirmLabel="Save"
                />
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

/**
 * @param {{
 *   fileUrls: string[],
 *   currentIndex: number,
 *   onSelectFile: (index: number) => void
 * }} props
 */
function ProteinFileList({ fileUrls, currentIndex, onSelectFile }) {
  if (fileUrls.length <= 1) return null

  return (
    <div className="flex flex-col text-sm border-1 border-border rounded-md">
      <div className="flex items-center justify-between px-3 py-2 border-b shrink-0">
        <span className="font-medium text-xs text-muted-foreground uppercase tracking-wide">Files ({fileUrls.length})</span>
      </div>
      <div className="flex flex-col">
        {fileUrls.map((fileUrl, index) => {
          const isSelected = index === currentIndex
          return (
            <Button
              key={index}
              variant="ghost"
              onClick={() => onSelectFile(index)}
              className={cn(
                "w-full justify-start gap-2 px-3 py-2 h-auto rounded-none border-b border-border last:border-0 font-normal",
                isSelected && "bg-primary/5 hover:bg-primary/5! text-primary hover:text-primary!"
              )}
            >
              <GiMolecule className="size-3.5 shrink-0" />
              <span className={cn("text-xs truncate flex-1 text-left")}>
                {formatFilename(fileUrl)}
              </span>
            </Button>
          )
        })}
      </div>
    </div>
  )
}

/** @param {Omit<React.ComponentProps<typeof ProteinViewerDialog>, 'file'> & { fileUrls?: string[], defaultFileUrl?: string| null }} props */
function ProteinViewer({ children, fileUrls = [], defaultFileUrl = null, ...props }) {
  const [representations, setRepresentations] = useState(() => DEFAULT_REPRESENTATIONS.map(withId))
  const { currentFileUrl, currentIndex, setCurrentIndex, hasPrev, hasNext, goToPrev, goToNext } = useFileNavigation(fileUrls, defaultFileUrl)

  const { data: file, isLoading } = useProteinFile(currentFileUrl)

  const title = currentFileUrl ? formatFilename(currentFileUrl) : ""

  return (
    <ProteinViewerDialog {...props} file={file} representations={representations}>
      {children}

      <ProteinViewerDialog.Content>
        <ProteinViewerDialog.Header>
          <ProteinViewerDialog.Title>{title}</ProteinViewerDialog.Title>
          <ProteinViewerDialog.Description>Interactive structure viewer</ProteinViewerDialog.Description>
        </ProteinViewerDialog.Header>

        <ProteinViewerDialog.ViewportWithPanel>
          <ProteinViewerDialog.Viewport className="relative">
            {isLoading && (
              <div className="absolute inset-0 z-50 flex flex-col items-center justify-center gap-3 bg-background/80 backdrop-blur">
                <Loader2 className="size-10 animate-spin text-muted-foreground" />
                <span className="text-sm text-muted-foreground">Loading protein structure...</span>
              </div>
            )}
            {fileUrls.length > 1 && (
              <>
                <Button
                  size="icon-lg"
                  variant="outline"
                  disabled={!hasPrev}
                  onClick={goToPrev}
                  className="absolute left-2 md:left-4 top-1/2 -translate-y-1/2 bg-background/80 backdrop-blur-sm shadow-lg hover:bg-background rounded-full z-50 size-8 md:size-10"
                >
                  <ChevronLeft className="size-4 md:size-5" />
                </Button>
                <Button
                  size="icon-lg"
                  variant="outline"
                  disabled={!hasNext}
                  onClick={goToNext}
                  className="absolute right-2 md:right-4 top-1/2 -translate-y-1/2 bg-background/80 backdrop-blur-sm shadow-lg hover:bg-background rounded-full z-50 size-8 md:size-10"
                >
                  <ChevronRight className="size-4 md:size-5" />
                </Button>
              </>
            )}
          </ProteinViewerDialog.Viewport>
          <ProteinViewerDialog.SidePanel className="gap-3">
            <ProteinRepresentationsPanel
              representations={representations}
              onChange={setRepresentations}
            />
            <ProteinFileList
              fileUrls={fileUrls}
              currentIndex={currentIndex}
              onSelectFile={setCurrentIndex}
            />
          </ProteinViewerDialog.SidePanel>
        </ProteinViewerDialog.ViewportWithPanel>

        <ProteinViewerDialog.Footer className="flex flex-row justify-between sm:justify-between!">
          <ProteinViewerDialog.ActionGroup>
            <ProteinAutoSpinAction />
            <ProteinCenterStageAction />
          </ProteinViewerDialog.ActionGroup>

          <ProteinViewerDialog.ActionGroup>
            <ProteinDownloadAction />
            <ProteinSnapshotAction />
          </ProteinViewerDialog.ActionGroup>
        </ProteinViewerDialog.Footer>
      </ProteinViewerDialog.Content>
    </ProteinViewerDialog>
  )
}

/** @param {React.ComponentProps<typeof ProteinViewerDialog.Trigger>} props */
function ProteinViewerTrigger({ children, ...props }) {
  return (
    <ProteinViewerDialog.Trigger {...props}>
      {children}
    </ProteinViewerDialog.Trigger>
  )
}

ProteinViewer.Trigger = ProteinViewerTrigger;

export {
  ProteinViewer,
  ProteinViewerTrigger,
}
